import React from 'react';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/common/PageHeader';
import { SEO } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';

const Privacy = () => {
  const { isRTL } = useLanguage();

  return (
    <Layout>
      <SEO titleKey="seo.privacy.title" descriptionKey="seo.privacy.description" noindex={true} />
      <PageHeader
        title="Privacy Policy"
        titleAr="سياسة الخصوصية"
        subtitle="How we collect, use, and protect your information"
        subtitleAr="كيف نجمع ونستخدم ونحمي معلوماتك"
        size="sm"
      />

      <div className="container py-12 max-w-4xl" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="prose prose-emerald dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '1. المعلومات التي نجمعها' : '1. Information We Collect'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'نجمع المعلومات التالية: المعلومات الشخصية (الاسم، البريد الإلكتروني، رقم الهاتف)، بيانات الملف الشخصي، معلومات الدفع، وبيانات استخدام المنصة.'
                : 'We collect the following information: personal details (name, email, phone number), profile data, payment information, and platform usage data.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '2. كيف نستخدم معلوماتك' : '2. How We Use Your Information'}
            </h2>
            <ul className="text-muted-foreground space-y-2">
              <li>{isRTL ? 'توفير وتحسين خدماتنا التعليمية' : 'To provide and improve our educational services'}</li>
              <li>{isRTL ? 'معالجة المدفوعات والحجوزات' : 'To process payments and bookings'}</li>
              <li>{isRTL ? 'التواصل معك بشأن حسابك وجلساتك' : 'To communicate with you about your account and sessions'}</li>
              <li>{isRTL ? 'تخصيص تجربة التعلم الخاصة بك' : 'To personalize your learning experience'}</li>
              <li>{isRTL ? 'ضمان سلامة وأمن المنصة' : 'To ensure the safety and security of the platform'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '3. حماية البيانات' : '3. Data Protection'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'نستخدم تشفير SSL/TLS لحماية بياناتك أثناء النقل. يتم تخزين البيانات بشكل آمن مع ضوابط وصول صارمة. نلتزم بأفضل الممارسات الأمنية لحماية معلوماتك.'
                : 'We use SSL/TLS encryption to protect your data in transit. Data is stored securely with strict access controls. We adhere to security best practices to safeguard your information.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '4. مشاركة البيانات' : '4. Data Sharing'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'لا نبيع معلوماتك الشخصية لأطراف ثالثة. قد نشارك البيانات مع: معالجي الدفع لإتمام المعاملات، المعلمين لغرض تقديم الجلسات، ومقدمي الخدمات الذين يساعدون في تشغيل المنصة.'
                : 'We do not sell your personal information to third parties. We may share data with: payment processors to complete transactions, teachers for session delivery, and service providers who help operate the platform.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '5. ملفات تعريف الارتباط' : '5. Cookies'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'نستخدم ملفات تعريف الارتباط لتحسين تجربتك على المنصة، تذكر تفضيلاتك، وتحليل استخدام المنصة. يمكنك إدارة إعدادات ملفات تعريف الارتباط في متصفحك.'
                : 'We use cookies to enhance your experience on the platform, remember your preferences, and analyze platform usage. You can manage cookie settings in your browser.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '6. حقوقك' : '6. Your Rights'}
            </h2>
            <ul className="text-muted-foreground space-y-2">
              <li>{isRTL ? 'الوصول إلى بياناتك الشخصية وطلب نسخة منها' : 'Access your personal data and request a copy'}</li>
              <li>{isRTL ? 'تصحيح أو تحديث بياناتك الشخصية' : 'Correct or update your personal data'}</li>
              <li>{isRTL ? 'طلب حذف حسابك وبياناتك' : 'Request deletion of your account and data'}</li>
              <li>{isRTL ? 'الانسحاب من الاتصالات التسويقية' : 'Opt out of marketing communications'}</li>
              <li>{isRTL ? 'تقديم شكوى إلى السلطة الإشرافية المختصة' : 'Lodge a complaint with the relevant supervisory authority'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '7. بيانات الأطفال' : '7. Children\'s Data'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'نأخذ حماية بيانات الأطفال على محمل الجد. يمكن للوالدين إنشاء حسابات عائلية لإدارة حسابات أطفالهم مع ضوابط وصول مناسبة.'
                : 'We take children\'s data protection seriously. Parents can create family accounts to manage their children\'s accounts with appropriate access controls.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '8. الاحتفاظ بالبيانات' : '8. Data Retention'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'نحتفظ ببياناتك الشخصية طالما كان حسابك نشطاً أو حسب الحاجة لتقديم خدماتنا. يمكنك طلب حذف بياناتك في أي وقت من خلال إعدادات حسابك أو بالتواصل مع فريق الدعم.'
                : 'We retain your personal data for as long as your account is active or as needed to provide our services. You can request data deletion at any time through your account settings or by contacting our support team.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '9. الاتصال بنا' : '9. Contact Us'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'لأي أسئلة تتعلق بالخصوصية، يرجى التواصل معنا عبر: privacy@hudanlilmuttaqeen.com'
                : 'For any privacy-related questions, please contact us at: privacy@hudanlilmuttaqeen.com'}
            </p>
          </section>

          <p className="text-sm text-muted-foreground pt-6 border-t">
            {isRTL ? 'آخر تحديث: مارس 2026' : 'Last updated: March 2026'}
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Privacy;
